import React, { useMemo } from 'react';
import {
  CustomSlotConfig,
  resolveSlotConfig,
  SlotApiConfig,
  SlotBookingConfig,
  SlotSource,
} from '../../CustomBlocks/slotBooking/slotTypes';
import { CalendarIcon, ClockIcon, GlobeIcon } from '../../shared/Icons';
import { SegmentedControl, SettingsGroup, SettingsRow, Switch } from './settingsUi';
import { EditorRoot, GroupHeader, TILE } from './SlotBookingEditor.styles';
import ApiSourceSection from './ApiSourceSection';
import CustomSourceSection from './CustomSourceSection';

interface SlotBookingEditorProps {
  value?: Partial<SlotBookingConfig>;
  onChange: (next: SlotBookingConfig) => void;
}

const SOURCE_OPTIONS: { value: SlotSource; label: string; icon: React.ReactNode }[] = [
  { value: 'custom', label: 'Custom hours', icon: <CalendarIcon size={15} /> },
  { value: 'api', label: 'From API', icon: <GlobeIcon size={15} /> },
];

const SlotBookingEditor: React.FC<SlotBookingEditorProps> = ({ value, onChange }) => {
  const config = useMemo(() => resolveSlotConfig(value), [value]);

  const update = (patch: Partial<SlotBookingConfig>) => onChange({ ...config, ...patch });
  const updateApi = (patch: Partial<SlotApiConfig>) => update({ api: { ...config.api, ...patch } });
  const updateCustom = (patch: Partial<CustomSlotConfig>) =>
    update({ custom: { ...config.custom, ...patch } });

  return (
    <EditorRoot>
      <GroupHeader>
        <span className="sb-group-title">Availability source</span>
      </GroupHeader>
      <SegmentedControl
        value={config.source}
        options={SOURCE_OPTIONS}
        onChange={(source) => update({ source })}
      />

      {config.source === 'api' ? (
        <ApiSourceSection value={config.api} onChange={updateApi} />
      ) : (
        <CustomSourceSection value={config.custom} onChange={updateCustom} />
      )}

      <SettingsGroup title="Respondent view">
        <SettingsRow
          icon={<GlobeIcon size={16} />}
          iconColor={TILE.blue}
          label="Timezone picker"
          description="Let respondents switch timezone"
        >
          <Switch
            checked={config.showTimezone}
            label="Timezone picker"
            onChange={(showTimezone) => update({ showTimezone })}
          />
        </SettingsRow>
        <SettingsRow
          icon={<ClockIcon size={16} />}
          iconColor={TILE.gray}
          label="24-hour clock"
          description="Show 14:30 instead of 2:30 PM"
        >
          <Switch
            checked={config.use24Hour}
            label="24-hour clock"
            onChange={(use24Hour) => update({ use24Hour })}
          />
        </SettingsRow>
      </SettingsGroup>
    </EditorRoot>
  );
};

export default SlotBookingEditor;
